import React from 'react';
import Layout from '../components/common/Layout';
import {
    ArrowRight,
    Shield,
    Zap,
    Users,
    BarChart3,
    CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/common/Button';

const HomePage = () => {
    const { isAuthenticated, user } = useAuth();

    const features = [
        {
            icon: Shield,
            title: 'Seguridad Avanzada',
            description: 'Autenticación JWT y encriptación de datos de primera clase'
        },
        {
            icon: Zap,
            title: 'Rendimiento Óptimo',
            description: 'Aplicación rápida y eficiente construida con las mejores tecnologías'
        },
        {
            icon: Users,
            title: 'Gestión de Usuarios',
            description: 'Sistema completo de gestión y administración de usuarios'
        },
        {
            icon: BarChart3,
            title: 'Analytics Integrado',
            description: 'Dashboards y reportes para monitorear tu negocio'
        }
    ];

    return (
        <Layout>
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            Bienvenido a <span className="text-blue-200">Argus</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
                            Sistema completo de autenticación y gestión empresarial.
                            Seguro, rápido y fácil de usar.
                        </p>

                        {isAuthenticated ? (
                            <div className="space-y-4">
                                <p className="text-blue-200 text-lg">
                                    ¡Hola, {user?.full_name}! 👋
                                </p>
                                <Link to="/dashboard">
                                    <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
                                        Ir al Dashboard
                                        <ArrowRight className="ml-2 h-5 w-5" />
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="/register">
                                    <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
                                        Comenzar Gratis
                                        <ArrowRight className="ml-2 h-5 w-5" />
                                    </Button>
                                </Link>
                                <Link to="/login">
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        className="border-white text-white hover:bg-white hover:text-blue-600"
                                    >
                                        Iniciar Sesión
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Características Principales
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Todo lo que necesitas para gestionar tu negocio de manera eficiente y segura
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <div key={index} className="text-center group">
                                    <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                                        <Icon className="h-8 w-8 text-blue-600" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                        {feature.title}
                                    </h3>
                                    <p className="text-gray-600">
                                        {feature.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            {isAuthenticated && (
                <div className="py-16 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">
                                Tu Actividad
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                                <h3 className="text-2xl font-bold text-gray-900">Conectado</h3>
                                <p className="text-gray-600">Estado de la sesión</p>
                            </div>

                            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                                <Users className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                                <h3 className="text-2xl font-bold text-gray-900">1</h3>
                                <p className="text-gray-600">Usuario activo</p>
                            </div>

                            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                                <BarChart3 className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                                <h3 className="text-2xl font-bold text-gray-900">100%</h3>
                                <p className="text-gray-600">Sistema funcionando</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default HomePage;